import validateCategoryParameters from './validateCategoryParameters';
import updateConversationsCategoriesService from '../../../services/updateConversationsCategoriesService';
import conversationsCategoriesStoreUpdate from 'owa-mail-actions/lib/triage/conversationsCategoriesStoreUpdate';
import {
    getApplyConversationActionContext,
    getContextFolderIdForTable,
    TableView,
} from 'owa-mail-list-store';
import { ActionSource } from 'owa-mail-store';
import * as undoActions from 'owa-mail-undo';
import { processApplyConversationActionResponseMessage } from 'owa-mail-triage-action-response-processor';
import tombstoneOperations, { TombstoneReasonType } from 'owa-mail-list-tombstone';

/**
 * Categorize the conversations from table
 * @param conversationRowKeys to be categorized
 * @param tableView that contain the conversations
 * @param categorizesToAdd the categories to add to the conversations
 * @param categorizesToRemove the categories to be removed from the conversations
 * @param clearCategories a boolean which indicates whether to clear categories for the specified conversations
 * @param actionSource where the action is triggered from
 * @param promiseToWaitUpon promise to wait upon before sending the update category request.
 */
export default function updateConversationsCategoriesInternal(
    conversationRowKeys: string[],
    tableView: TableView,
    categoriesToAdd: string[],
    categoriesToRemove: string[],
    clearCategories: boolean,
    actionSource: ActionSource,
    promiseToWaitUpon: Promise<boolean>
): Promise<void> {
    // Validate category parameters and throw error if they are invalid
    validateCategoryParameters(
        conversationRowKeys,
        categoriesToAdd,
        categoriesToRemove,
        clearCategories
    );

    undoActions.clearLastUndoableAction();

    if (promiseToWaitUpon) {
        // In case where user creates a category from the commanding bar drop down
        // we want to only send the request to apply the category only if the creation of
        // category succeeds. Today the promiseToWaitUpon here is create Category action
        return promiseToWaitUpon.then(isPromiseSuccessful => {
            return submitRequestAndMakeLocalUpdate(
                conversationRowKeys,
                tableView,
                categoriesToAdd,
                categoriesToRemove,
                clearCategories,
                actionSource
            );
        });
    } else {
        return submitRequestAndMakeLocalUpdate(
            conversationRowKeys,
            tableView,
            categoriesToAdd,
            categoriesToRemove,
            clearCategories,
            actionSource
        );
    }
}

function submitRequestAndMakeLocalUpdate(
    conversationRowKeys: string[],
    tableView: TableView,
    categoriesToAdd: string[],
    categoriesToRemove: string[],
    clearCategories: boolean,
    actionSource: ActionSource
) {
    // 1. Local update the conversation categories in store
    conversationsCategoriesStoreUpdate(
        conversationRowKeys,
        tableView.id,
        categoriesToAdd,
        categoriesToRemove,
        clearCategories
    );

    // Add category operation to tombstone
    // 2. The local update operation should happen before adding category to tombstone
    // else we may not have correct set of categories when adding it to the tombstone
    addCategoryOperationToTombstone(conversationRowKeys, tableView);

    // 3. submit server request
    return submitUpdateConversationsCategories(
        conversationRowKeys,
        tableView,
        categoriesToAdd,
        categoriesToRemove,
        clearCategories,
        actionSource
    );
}

function submitUpdateConversationsCategories(
    conversationRowKeys: string[],
    tableView: TableView,
    categoriesToAdd: string[],
    categoriesToRemove: string[],
    clearCategories: boolean,
    actionSource: ActionSource
): Promise<void> {
    const contextFolderId = getContextFolderIdForTable(tableView);
    const conversationActionContexts = conversationRowKeys.map(rowKey =>
        getApplyConversationActionContext(rowKey, tableView.id)
    );

    const updateCategoriesPromise = updateConversationsCategoriesService(
        conversationActionContexts,
        contextFolderId,
        categoriesToAdd,
        categoriesToRemove,
        clearCategories
    );

    return processApplyConversationActionResponseMessage(
        updateCategoriesPromise,
        conversationRowKeys,
        contextFolderId,
        tableView,
        /*
         * For conversations we see that we get notifications for category update
         * with partial (or incorrect) category set even after the action completes
         * Hence we do not want to remove this entry from tombstone when action completes
         */
        false /* shouldRemoveRowsFromTombstone */
    );
}

function addCategoryOperationToTombstone(conversationRowKeys: string[], tableView: TableView) {
    // Add each row to the tombstone map
    tombstoneOperations.add(
        conversationRowKeys,
        tableView.serverFolderId,
        TombstoneReasonType.Category
    );
}
